
import React, { useState, useCallback, useMemo } from "react";
import { DataToolbar, ActionButtons } from "../components/DataToolbar";
import { PreviewModal } from "../components/PreviewModal";
import { Modal } from "../components/Modal";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { useAiContext } from "../ai/AiContext"; 
import { useToast } from "../components/Toast";
import { seedBrandAssets, type BrandAsset } from "../utils/brandAssetImport";
import { useAuth } from "../context/AuthContext";
import { exportBrandAssetsToCsv, importBrandAssetsFromCsv } from "../services/brandAssetsImportExport";
import { DateField } from "../components/form/DateField";
import { formatDate, parseDate, formatExcelOrTimestamp } from "../utils/dateUtils";
import { canEditModule } from "../config/roles";
import { IpMattersPage } from "./IpMattersPage"; 
import { UploadDropzone, UploadedFile } from "../components/shared/UploadDropzone";
import { classifyBrandSegment, SEGMENT_COLORS, SEGMENT_LABELS, BrandSegmentKind } from "../services/brandSegment";

import { db, serverTimestamp } from "../../firebaseClient";
import { logLegalActivityOnChange } from "../services/legalActivityLogger";

export function BrandAssetsPage() {
  const { user } = useAuth();
  const canEdit = user ? canEditModule(user.role, 'BRAND_ASSETS') : false;

  const [activeTab, setActiveTab] = useState<'list' | 'map' | 'ip-rights'>('list');
  const [items, setItems] = useState<BrandAsset[]>([]);
  const [filtered, setFiltered] = useState<BrandAsset[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<BrandAsset | null>(null);
  const [previewItem, setPreviewItem] = useState<BrandAsset | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  
  const { requestDelete, setActiveItem } = useAiContext();
  const { showToast } = useToast();
  
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Check URL params for tab navigation
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "ip-rights" || params.get("tab") === "ip") {
        setActiveTab("ip-rights");
    }
  }, []);

  // Fetch Data Function (Manual Trigger to support "Refresh Button")
  const fetchBrandAssets = useCallback(async () => {
    setLoading(true);
    try {
        console.log("[BrandAssets] Fetching data from Firestore...");
        const q = db.collection("brand_assets").orderBy("createdAt", "desc");
        const snapshot = await q.get();
        
        if (snapshot.empty) {
             setItems([]);
             setFiltered([]);
        } else {
             const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BrandAsset));
             setItems(list);
             setFiltered(list);
        }
        setError(null);
    } catch (err: any) {
        console.error("Brand Assets fetch error:", err);
        setError(err);
    } finally {
        setLoading(false);
    }
  }, []);

  // Initial Load
  React.useEffect(() => {
    fetchBrandAssets();
  }, [fetchBrandAssets]);

  const getExpiryStatus = (dateVal?: any) => {
      if (!dateVal) return { color: "var(--muted)", text: "未設定" };
      
      const dateStr = formatExcelOrTimestamp(dateVal);
      if (dateStr === "-" || !dateStr) return { color: "var(--muted)", text: "未設定" };

      const days = (new Date(dateStr).getTime() - new Date().getTime()) / (1000 * 3600 * 24);
      
      if (isNaN(days)) return { color: "var(--muted)", text: dateStr }; 

      if (days < 0) return { color: "var(--danger)", text: `${dateStr} (已過期)` };
      if (days <= 90) return { color: "var(--danger)", text: `${dateStr} (90天內)` };
      if (days <= 180) return { color: "#fbbf24", text: `${dateStr} (180天內)` };
      return { color: "var(--ok)", text: dateStr };
  };

  const handleDelete = (id: string, name: string) => {
    requestDelete({
      targetType: 'brandAsset',
      targetId: id,
      summary: name,
      onConfirm: async () => {
        try {
          await db.collection("brand_assets").doc(id).delete();
          await logLegalActivityOnChange({
              module: "品牌資產",
              docId: id,
              title: name,
              action: "刪除",
              userEmail: user?.email || "unknown"
          });
          showToast("已刪除");
          fetchBrandAssets(); // Refresh after delete
        } catch(e) { console.error(e); }
      }
    });
  };

  const handleExportCsv = async () => {
    try {
      const csv = await exportBrandAssetsToCsv();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `brand_assets_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showToast("匯出失敗", "error");
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setImportError(null);
    try {
      setIsImporting(true);
      const { created, updated, skipped } = await importBrandAssetsFromCsv(importFile);
      
      if (created === 0 && updated === 0) {
          throw new Error(`匯入完成但未寫入任何資料 (略過: ${skipped})。請檢查 CSV 欄位名稱是否正確。`);
      }

      if (user) {
        await logLegalActivityOnChange({
            module: "品牌資產",
            docId: "import-" + Date.now(),
            title: `批量匯入: 新增${created}筆/更新${updated}筆`,
            action: "新增",
            userEmail: user.email
        });
      }

      showToast(`匯入成功：新增 ${created} 筆，更新 ${updated} 筆`);
      setIsImportModalOpen(false);
      setImportFile(null);
      fetchBrandAssets(); // Refresh after import
    } catch (err: any) {
      console.error("Import error:", err);
      setImportError(err.message || "匯入失敗，請確認檔案格式");
    } finally {
      setIsImporting(false);
    }
  };

  const handleDropzoneChange = (files: UploadedFile[]) => {
      if (files.length > 0) {
          setImportFile(files[0].rawFile);
          setImportError(null);
      } else {
          setImportFile(null);
      }
  };

  const dropzoneFiles: UploadedFile[] = importFile ? [{
      name: importFile.name,
      size: importFile.size,
      type: importFile.type,
      rawFile: importFile
  }] : [];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="row" style={{ borderBottom: '1px solid #333', marginBottom: 16, flexShrink: 0 }}>
        <button 
          className="navbtn" 
          style={{ width: 'auto', borderBottom: activeTab === 'list' ? '2px solid #fff' : 'none', borderRadius: 0, color: activeTab === 'list' ? '#fff' : 'var(--muted)' }}
          onClick={() => setActiveTab('list')}
        >
          品牌資產列表
        </button>
        <button 
          className="navbtn" 
          style={{ width: 'auto', borderBottom: activeTab === 'map' ? '2px solid #fff' : 'none', borderRadius: 0, color: activeTab === 'map' ? '#fff' : 'var(--muted)' }}
          onClick={() => setActiveTab('map')}
        >
          品牌佈局導圖
        </button>
        <button 
          className="navbtn" 
          style={{ width: 'auto', borderBottom: activeTab === 'ip-rights' ? '2px solid #fff' : 'none', borderRadius: 0, color: activeTab === 'ip-rights' ? '#fff' : 'var(--muted)' }}
          onClick={() => setActiveTab('ip-rights')}
        >
          智財申請與維權
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {activeTab === 'list' && (
          <>
            <DataToolbar 
              canEdit={canEdit} 
              onCreate={() => { setEditingItem(null); setIsModalOpen(true); }} 
              onExport={handleExportCsv} 
              onImport={() => { setIsImportModalOpen(true); setImportError(null); setImportFile(null); }}
            />
            
            {loading && <div className="p" style={{padding:20, textAlign:'center'}}>載入中...</div>}
            {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}

            {!loading && (
              <div style={{ display: "grid", gap: 10 }}>
                {filtered.map((row) => {
                   const exp = getExpiryStatus(row.expiryDate);
                   const segKind = classifyBrandSegment(row);
                   // Display classification label, fallback to "未分類" if other
                   const segmentLabel = SEGMENT_LABELS[segKind] || "未分類";
                   
                   return (
                      <div key={row.id} className="tile" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                           <div style={{ fontSize: 16, fontWeight: "bold", color: "#fff" }}>{row.name}</div>
                           <div className="row" style={{ marginTop: 6, fontSize: 13, color: "var(--muted)", gap: 12 }}>
                              <span className="badge" style={{ background: SEGMENT_COLORS[segKind], border:"none", color:"#fff" }}>
                                {segmentLabel}
                              </span>
                              <span>{row.type}</span>
                              <span>負責: {row.owner || "未設定"}</span>
                           </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                           <div style={{ fontSize: 12, color: exp.color }}>到期: {exp.text}</div>
                           <ActionButtons canEdit={canEdit} onPreview={() => setPreviewItem(row)} onEdit={() => { setEditingItem(row); setIsModalOpen(true); }} onDelete={() => handleDelete(row.id!, row.name)} />
                        </div>
                      </div>
                   );
                })}
                {!error && filtered.length === 0 && <div className="p" style={{textAlign:"center", padding:20}}>目前無資料，請新增或匯入。</div>}
              </div>
            )}
          </>
        )}

        {activeTab === 'map' && (
            <BrandMap 
                items={items} 
                onRefresh={fetchBrandAssets} 
                isLoading={loading} 
            />
        )}
        
        {activeTab === 'ip-rights' && (
            <div style={{paddingTop: 10}}>
                <IpMattersPage />
            </div>
        )}
      </div>

      {previewItem && <PreviewModal data={previewItem} onClose={() => setPreviewItem(null)} />}
      
      {isModalOpen && (
        <AssetForm 
          initialData={editingItem} 
          onClose={() => setIsModalOpen(false)} 
          onSuccess={() => { showToast(editingItem ? "已更新" : "已新增"); setIsModalOpen(false); fetchBrandAssets(); }} 
        />
      )}

      {isImportModalOpen && (
        <Modal title="匯入品牌資產" onClose={() => setIsImportModalOpen(false)} width={550}>
           <div className="p" style={{ marginBottom: 20, lineHeight: 1.6, color: "var(--muted)" }}>
             CSV 欄位建議 (第一列)：<br/>
             <code style={{display:'block', padding:10, background:'#222', borderRadius:6, marginTop:8, color: '#a5b4fc', fontSize: 13}}>
                brandName,category,segment,type,region,owner,status,expiryDate,note
             </code>
           </div>
           
           {importError && (
             <div className="err" style={{marginBottom: 20}}>
                {importError}
             </div>
           )}

           <div style={{marginBottom: 20}}>
              <UploadDropzone
                  files={dropzoneFiles}
                  onFilesChange={handleDropzoneChange}
                  accept=".csv,text/csv"
                  maxFiles={1}
              />
           </div>
           
           <div className="row" style={{ justifyContent: "flex-end" }}>
             <button 
                type="button"
                className="btn" 
                style={{ 
                    width: 'auto', 
                    background: 'transparent', 
                    border: '1px solid #555', 
                    color: '#e5e5e5', 
                    marginRight: 10 
                }} 
                onClick={() => setIsImportModalOpen(false)}
             >
                取消
             </button>
             <button className="btn" style={{ width: 'auto' }} onClick={handleImportSubmit} disabled={!importFile || isImporting}>
               {isImporting ? "處理中..." : "開始匯入"}
             </button>
           </div>
        </Modal>
      )}
    </div>
  );
}

function BrandMap({ items, onRefresh, isLoading }: { items: BrandAsset[], onRefresh: () => void, isLoading: boolean }) {
  
  const handleRefreshLayout = () => {
      console.log("[BrandMap] 更新佈局按鈕被點擊");
      onRefresh();
  };

  const { owned, agency, licensed } = useMemo(() => {
    const list = items ?? [];

    // Deduplicate Map using name and segKind as unique key
    const map = new Map<string, BrandAsset>();

    for (const a of list) {
       if (!a) continue;
       // Robust name extraction: try 'name' first, then 'brandName'
       const name = (a.name ?? (a as any).brandName ?? "").trim();
       if (!name) continue;

       const segKind = classifyBrandSegment(a);
       const key = `${name}::${segKind}`;
       
       if (!map.has(key)) {
           map.set(key, { ...a, name } as BrandAsset);
       }
    }

    const uniques = Array.from(map.values());
    
    const sortFn = (arr: BrandAsset[]) => [...arr].sort((x, y) => (x.name || "").localeCompare(y.name || "", "zh-Hant"));

    const ownedArr: BrandAsset[] = [];
    const agencyArr: BrandAsset[] = [];
    const licensedArr: BrandAsset[] = [];

    for (const a of uniques) {
      const segKind = classifyBrandSegment(a);
      if (segKind === "owned") ownedArr.push(a);
      else if (segKind === "agency") agencyArr.push(a);
      else if (segKind === "licensed") licensedArr.push(a);
    }

    return { 
        owned: sortFn(ownedArr), 
        agency: sortFn(agencyArr), 
        licensed: sortFn(licensedArr) 
    };
  }, [items]);

  const NodeList = ({ list, color }: { list: BrandAsset[], color: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
       {list.map((b, idx) => (
         <div key={b.id || `b-${idx}`} style={{ background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: 6, borderLeft: `3px solid ${color}`, fontSize: 13 }}>
            <div style={{ fontWeight: 'bold' }}>{b.name}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{b.region} {b.category ? `(${b.category})` : ''}</div>
         </div>
       ))}
       {list.length === 0 && <div className="p" style={{fontSize:12}}>無資料</div>}
    </div>
  );

  return (
    <div className="card" style={{ width: '100%', minHeight: 600, padding: 30, background: '#1a1a1a', overflowX: 'auto', display:'flex', flexDirection:'column' }}>
       
       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, borderBottom:'1px solid #333', paddingBottom: 20 }}>
          <div style={{ background: '#333', color: '#fff', padding: '8px 24px', borderRadius: 20, fontWeight: 'bold', fontSize: 18, border: '1px solid #555' }}>
             上友事業品牌佈局
          </div>
          <button 
            type="button"
            className="smallbtn" 
            onClick={handleRefreshLayout}
            disabled={isLoading}
            style={{
                height: 36, 
                padding: '0 16px', 
                background: isLoading ? '#333' : '#fff', 
                color: isLoading ? '#888' : '#000', 
                border: 'none',
                fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: isLoading ? 'default' : 'pointer'
            }}
          >
            {isLoading ? "更新中..." : "🔄 更新佈局"}
          </button>
       </div>
       
       {isLoading ? (
           <div style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#888'}}>
               正在重新整理佈局...
           </div>
       ) : (
           <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 30 }}>
              <div style={{ background: '#222', padding: 16, borderRadius: 12, border: '1px solid #333' }}>
                 <h3 style={{ borderBottom: `2px solid ${SEGMENT_COLORS.owned}`, paddingBottom: 8, color: SEGMENT_COLORS.owned, margin: 0, fontSize: 16 }}>
                    {SEGMENT_LABELS.owned} ({owned.length})
                 </h3>
                 <NodeList list={owned} color={SEGMENT_COLORS.owned} />
              </div>
              <div style={{ background: '#222', padding: 16, borderRadius: 12, border: '1px solid #333' }}>
                 <h3 style={{ borderBottom: `2px solid ${SEGMENT_COLORS.agency}`, paddingBottom: 8, color: SEGMENT_COLORS.agency, margin: 0, fontSize: 16 }}>
                    {SEGMENT_LABELS.agency} ({agency.length})
                 </h3>
                 <NodeList list={agency} color={SEGMENT_COLORS.agency} />
              </div>
              <div style={{ background: '#222', padding: 16, borderRadius: 12, border: '1px solid #333' }}>
                 <h3 style={{ borderBottom: `2px solid ${SEGMENT_COLORS.licensed}`, paddingBottom: 8, color: SEGMENT_COLORS.licensed, margin: 0, fontSize: 16 }}>
                    {SEGMENT_LABELS.licensed} ({licensed.length})
                 </h3>
                 <NodeList list={licensed} color={SEGMENT_COLORS.licensed} />
              </div>
           </div>
       )}
    </div>
  );
}

function AssetForm({ initialData, onClose, onSuccess }: any) {
  const [form, setForm] = useState<Partial<BrandAsset>>(initialData || { group: "自有品牌" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const handleChange = (k: any, v: any) => setForm(p => ({ ...p, [k]: v }));
  const { user } = useAuth();

  const handleSave = async () => {
      setErrorMsg("");
      if (!form.name) {
          setErrorMsg("請填寫品牌/資產名稱");
          return;
      }

      setIsSubmitting(true);
      try {
          const payload = { ...form, lastUpdatedBy: user?.displayName || "User", lastUpdatedAt: serverTimestamp() };
          
          let docId = initialData?.id;
          const action = docId ? "修改" : "新增";

          if (docId) {
            await db.collection("brand_assets").doc(docId).update(payload);
          } else {
            if (!payload.assetNo) payload.assetNo = `BA-${Date.now()}`;
            if (!payload.status) payload.status = "有效";
            const ref = await db.collection("brand_assets").add({ ...payload, createdAt: serverTimestamp() });
            docId = ref.id;
          }

          if (user && docId) {
              await logLegalActivityOnChange({
                  module: "品牌資產",
                  docId: docId,
                  title: payload.name ?? "", 
                  action: action,
                  userEmail: user.email
              });
          }

          onSuccess();
      } catch (e: any) {
          console.error("Save failed:", e);
          setErrorMsg("儲存失敗: " + (e.message || "請稍後再試"));
      } finally {
          setIsSubmitting(false);
      }
  };

  return (
    <Modal title={initialData ? "編輯資產" : "新增資產"} onClose={onClose}>
       {errorMsg && <div className="err" style={{marginBottom:16}}>{errorMsg}</div>}
       <div className="grid">
          <div className="field"><label className="label">資產編號</label><input className="input" value={form.assetNo ?? ""} onChange={e=>handleChange("assetNo", e.target.value)} placeholder="(自動產生或輸入)" /></div>
          <div className="field"><label className="label">名稱 (必填)</label><input className="input" value={form.name ?? ""} onChange={e=>handleChange("name", e.target.value)} /></div>
          
          <div className="field"><label className="label">類別 (Category)</label>
             <input className="input" value={form.category ?? ""} onChange={e=>handleChange("category", e.target.value)} placeholder="House Brand / Licensed Brand / IP..." />
          </div>
          
          <div className="field"><label className="label">類型 (Type)</label><input className="input" value={form.type ?? ""} onChange={e=>handleChange("type", e.target.value)} placeholder="如: 商標、著作權、專利" /></div>
          <DateField label="到期日" value={parseDate(form.expiryDate ?? null)} onChange={d => handleChange("expiryDate", formatDate(d))} />
          <div className="field"><label className="label">負責人</label><input className="input" value={form.owner ?? ""} onChange={e=>handleChange("owner", e.target.value)} /></div>
          <div className="field"><label className="label">區域</label><input className="input" value={form.region ?? ""} onChange={e=>handleChange("region", e.target.value)} /></div>
          <div className="field"><label className="label">狀態</label><input className="input" value={form.status ?? ""} onChange={e=>handleChange("status", e.target.value)} /></div>
          <div className="field" style={{gridColumn:"1/-1"}}><label className="label">備註</label><textarea className="input" rows={2} value={form.notes ?? ""} onChange={e=>handleChange("notes", e.target.value)} /></div>
       </div>
       <div className="row" style={{marginTop:20, justifyContent: 'flex-end'}}>
           <button className="btn" style={{width: 'auto'}} disabled={isSubmitting} onClick={handleSave}>
               {isSubmitting ? "儲存中..." : "儲存"}
           </button>
       </div>
    </Modal>
  );
}
